import React, { useCallback, useEffect, useState } from 'react';
import { Organization, PaginatedResponse } from '../../types';
import { fetchOrganizations, createOrganization, updateOrganization, deleteOrganization } from '../../services/api';
import { useSuperTenant } from '../../context/SuperAdminTenantContext';

const INDUSTRIES = ['Pharma PCD', 'Pharma Retail', 'Pharma Distributor', 'Hospital Pharmacy', 'Institutional', 'Export'];
const TERRITORIES = [
  'Bihar', 'Uttar Pradesh', 'Madhya Pradesh', 'Maharashtra', 'Gujarat', 'Rajasthan',
  'Karnataka', 'Telangana', 'Tamil Nadu', 'West Bengal', 'Delhi NCR', 'Punjab',
  'Haryana', 'Odisha', 'Chhattisgarh', 'Jharkhand', 'Assam',
];

const initials = (name: string) =>
  name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

const fmt = (n?: number) =>
  n ? `₹${Number(n).toLocaleString('en-IN')}` : '₹0';

interface FormState {
  name: string;
  city: string;
  industry: string;
  territory: string;
  drug_licence_number: string;
  drug_licence_status: 'verified' | 'unverified' | 'expired';
  gst_number: string;
  website: string;
}

const emptyForm: FormState = {
  name: '', city: '', industry: '', territory: '',
  drug_licence_number: '', drug_licence_status: 'unverified',
  gst_number: '', website: '',
};

const OrganizationList: React.FC = () => {
  const { tenantId } = useSuperTenant();
  const [data, setData]         = useState<PaginatedResponse<Organization> | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<Organization | null>(null);
  const [form, setForm]         = useState<FormState>(emptyForm);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchOrganizations({ page, search: search || undefined, tenant_id: tenantId || undefined });
      setData(res);
    } catch { setError('Failed to load organizations'); }
    finally { setLoading(false); }
  }, [page, search, tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (o: Organization) => {
    setEditing(o);
    setForm({
      name: o.name, city: o.city ?? '', industry: o.industry ?? '',
      territory: o.territory ?? '', drug_licence_number: o.drug_licence_number ?? '',
      drug_licence_status: o.drug_licence_status, gst_number: o.gst_number ?? '',
      website: o.website ?? '',
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await updateOrganization(editing.id, form);
      else await createOrganization(form);
      setShowForm(false); load();
    } catch { alert('Failed to save organization'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this organization?')) return;
    try { await deleteOrganization(id); load(); }
    catch { alert('Failed to delete'); }
  };

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  return (
    <div className="list-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Organizations</h1>
          <p className="page-meta"><strong>{data?.total ?? 0}</strong> organizations</p>
        </div>
        <div className="page-hd-right">
          <div className="filter-input-wrap">
            <svg className="filter-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input className="filter-input" placeholder="Search organizations…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={openCreate}>+ New Organization</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="data-card">
        {loading ? <div className="spinner">Loading…</div> : (
          <>
            <div className="data-card-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Industry</th>
                    <th>Territory</th>
                    <th>Revenue Won</th>
                    <th>Open Deals</th>
                    <th>Drug Licence</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data && data.data.length > 0 ? data.data.map(o => (
                    <tr key={o.id}>
                      <td>
                        <div className="avatar-chip">
                          <span className="avatar-sm" style={{ background: 'var(--primary)', borderRadius: 6 }}>
                            {initials(o.name)}
                          </span>
                          <div>
                            <div className="avatar-sm-name">{o.name}</div>
                            {o.city && <div className="td-muted" style={{ fontSize: 11 }}>{o.city}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="td-muted">{o.industry || '—'}</td>
                      <td className="td-muted">{o.territory || '—'}</td>
                      <td style={{ color: 'var(--teal)', fontWeight: 600 }}>{fmt(o.revenue_won)}</td>
                      <td style={{ fontWeight: 600 }}>{o.open_deals_count ?? 0}</td>
                      <td>
                        {o.drug_licence_status === 'verified'
                          ? <span className="badge badge-qualified">Verified ✓</span>
                          : o.drug_licence_status === 'expired'
                          ? <span className="badge badge-lost">Expired</span>
                          : <span className="badge" style={{ background: 'var(--amber-light)', color: '#b07318' }}>Unverified ⚠</span>}
                      </td>
                      <td>
                        <div className="row-actions">
                          <button className="row-btn" onClick={() => openEdit(o)}>Edit</button>
                          <button className="row-btn row-btn-danger" onClick={() => handleDelete(o.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr className="table-empty-row">
                      <td colSpan={7}>No organizations yet. Click <strong>+ New Organization</strong> to add one.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="pager">
              <span>Organizations ({data?.total ?? 0})</span>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="deal-modal">
            <div className="deal-modal-header">
              <div className="deal-modal-header-inner">
                <div>
                  <h2 className="deal-modal-title">{editing ? 'Edit Organization' : 'New Organization'}</h2>
                  <p className="deal-modal-subtitle">Pharma distributor or retailer</p>
                </div>
                <button className="deal-modal-close" onClick={() => setShowForm(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            <form onSubmit={handleSave} className="deal-modal-body">
              <div className="deal-form-group">
                <label className="deal-form-label">Organization Name *</label>
                <input className="deal-form-input" name="name" value={form.name} onChange={handle} required placeholder="Kumar Pharma Distributors" />
              </div>
              <div className="deal-form-row">
                <div className="deal-form-group">
                  <label className="deal-form-label">City</label>
                  <input className="deal-form-input" name="city" value={form.city} onChange={handle} placeholder="Patna" />
                </div>
                <div className="deal-form-group">
                  <label className="deal-form-label">Territory (State)</label>
                  <select className="deal-form-input" name="territory" value={form.territory} onChange={handle}>
                    <option value="">Select…</option>
                    {TERRITORIES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="deal-form-row">
                <div className="deal-form-group">
                  <label className="deal-form-label">Industry</label>
                  <select className="deal-form-input" name="industry" value={form.industry} onChange={handle}>
                    <option value="">Select…</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div className="deal-form-group">
                  <label className="deal-form-label">Drug Licence Status</label>
                  <select className="deal-form-input" name="drug_licence_status" value={form.drug_licence_status} onChange={handle}>
                    <option value="unverified">Unverified</option>
                    <option value="verified">Verified</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
              <div className="deal-form-row">
                <div className="deal-form-group">
                  <label className="deal-form-label">Drug Licence Number</label>
                  <input className="deal-form-input" name="drug_licence_number" value={form.drug_licence_number} onChange={handle} placeholder="DL-BI-123456" />
                </div>
                <div className="deal-form-group">
                  <label className="deal-form-label">GST Number</label>
                  <input className="deal-form-input" name="gst_number" value={form.gst_number} onChange={handle} placeholder="22AAAAA0000A1Z5" />
                </div>
              </div>
              <div className="deal-form-group">
                <label className="deal-form-label">Website</label>
                <input className="deal-form-input" name="website" value={form.website} onChange={handle} placeholder="https://kumarpharma.in" />
              </div>
              <div className="deal-modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="btn-spinner" />Saving…</> : editing ? 'Save Changes' : 'Add Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationList;
